import { css } from 'lit';
import componentStyles from '../styles/component.styles';
import blockStyles from '../styles/block.styles';
export default css `
  ${componentStyles}
  ${blockStyles}
  /**
  --------------------------------------------------
  * General
  --------------------------------------------------
  */
  :host([design-mode]) {
    pointer-events: none;
  }
  :host {
    container-type: inline-size;
  }

  .video-list {
    list-style: none;
  }

  .video-list.grid .container .wrapper,
  .ghost-grid {
    display: grid;
    gap: var(--p-video-grid-gap);
    grid-template-columns: repeat(auto-fill, minmax(var(--p-video-item-default-width), 1fr));
  }

  .video-list.grid.layout-portrait .container .wrapper,
  .layout-portrait .ghost-grid {
    grid-template-columns: repeat(auto-fill, minmax(var(--p-video-item-portrait-width), 1fr));
  }

  .video-list.grid.layout-card .container .wrapper,
  .layout-card .ghost-grid {
    grid-template-columns: repeat(auto-fill, minmax(var(--p-video-item-card-width), 1fr));
  }

  .video-list.grid.layout-full .container .wrapper,
  .layout-full .ghost-grid {
    grid-template-columns: repeat(auto-fill, minmax(var(--p-video-item-full-width, 100%), 1fr));
  }

  .video-list.slider.layout-full .ghost-grid {
    grid-template-columns: 1fr;
  }

  @container (max-width: 450px) {
    .video-list.grid {
      --p-video-item-default-width: 100%;
      --p-video-item-portrait-width: 100%;
      --p-video-item-card-width: 100%;
      --p-video-item-full-width: 100%;
    }
  }

  header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
    align-items: center;
  }

  h2 {
    font-family: var(--p-h5-font);
    font-size: var(--p-h5-font-size);
    font-weight: var(--p-h5-font-weight);
    font-style: var(--p-h5-font-style);
    line-height: var(--p-h5-line-height);
    text-transform: var(--p-h5-transform);
    letter-spacing: var(--p-h5-letter-spacing);
    margin: 0;
    padding-right: 1rem; // so not to brush up against the show all-btn on smaller devices
  }

  :host([dark]) {
    --p-section-bg: var(--p-bg-dark);
    --p-text-color: var(--p-text-color-light);
  }

  /**
--------------------------------------------------
* Type: Grid
--------------------------------------------------
*/
  .video-list.grid {
    width: min(var(--p-container-width), 100% - 2 * var(--p-gap));
    margin-inline: auto;
  }

  /**
--------------------------------------------------
* Type: Slider
--------------------------------------------------
*/
  .video-list.slider {
    width: min(var(--p-container-width), 100% - 2 * var(--p-gap));
    margin-inline: auto;
  }

  /* Desktop */
  @container (min-width: 768px) {
    .video-list.slider .container-outer {
      position: relative;
    }

    .video-list.slider .container {
      position: relative;
    }

    .video-list.slider .container .wrapper {
      display: flex;
      width: 100%;
      padding-top: 6px; /* so the focus ring is not hidden at the top */
      overflow: auto;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch; /* Needed to work on iOS Safari */

      /* Remove scrollbar */
      scrollbar-width: none;
      scrollbar-color: transparent transparent;
      padding-bottom: 10px;
      -ms-overflow-style: -ms-autohiding-scrollbar;
    }

    .video-list.slider .container .wrapper::-webkit-scrollbar {
      display: none;
      -webkit-appearance: none;
      width: 0;
      height: 0;
    }

    .video-list.slider .container .wrapper p-video-item {
      min-width: var(--ghost-item-width);
      max-width: var(--ghost-item-width);
      margin-right: var(--p-video-grid-gap);
      scroll-snap-align: start;
    }

    .video-list.slider .container .wrapper p-video-item:last-child {
      margin-right: 0;
    }

    .video-list.slider .slider-nav-left,
    .video-list.slider .slider-nav-right {
      position: absolute;
      display: block;
      top: 50%;
      z-index: 10;
      user-select: none;
      padding: 0px;
      cursor: pointer;
      background-color: var(--p-section-bg);
      color: var(--p-text-color);
      width: 40px;
      height: 40px;
      border-radius: 50%;
      opacity: 1;
      transition: opacity 200ms;
      display: flex;
      justify-content: center;
      align-items: center;
      box-shadow: 0 4px 18px rgba(0, 0, 0, 0.2);
      border: 1px solid var(--p-border);
    }

    :host([dark]) .video-list.slider .slider-nav-left,
    :host([dark]) .video-list.slider .slider-nav-right {
      box-shadow: 0 6px 14px rgba(0, 0, 0, 0.5);
      filter: brightness(150%);
      border: none;
    }

    .video-list.slider .slider-nav-left[disabled],
    .video-list.slider .slider-nav-right[disabled] {
      opacity: 0;
      pointer-events: none;
    }

    .video-list.slider .slider-nav-left:hover,
    .video-list.slider .slider-nav-right:hover {
      filter: brightness(90%);
    }

    :host([dark]) .video-list.slider .slider-nav-left:hover,
    :host([dark]) .video-list.slider .slider-nav-right:hover {
      filter: brightness(200%);
    }

    :host([dark]) .video-list.slider .slider-nav-left:focus-visible,
    :host([dark]) .video-list.slider .slider-nav-right:focus-visible {
      outline: 2px solid var(--p-text-color);
      outline-offset: 2px;
    }

    .video-list.slider .slider-nav-left {
      left: 0;
      translate: -50% -50%;
    }

    .video-list.slider .slider-nav-left > svg {
      transform: rotate(180deg);
    }

    .video-list.slider .slider-nav-right {
      right: 0;
      translate: 50% -50%;
    }
  }

  /* Mobile */
  @container (max-width: 768px) {
    .video-list.slider .container {
      margin: 0 calc(-1 * var(--p-gap));
    }

    .video-list.slider .slider-nav-left,
    .video-list.slider .slider-nav-right {
      display: none;
    }

    .video-list.slider .wrapper {
      width: 100%;
      overflow-x: auto;
      display: flex;
      gap: var(--p-video-grid-gap);
      padding-right: var(--p-gap);
      padding-left: var(--p-gap);
      padding-bottom: 0.6rem;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch; /* Needed to work on iOS Safari */
    }

    .video-list.slider .wrapper p-video-item {
      min-width: max(29vw, var(--p-video-item-default-width));
      scroll-snap-align: center; /* otherwise it wont work with out left start margin */
    }
  }

  @container (max-width: 685px) {
    .video-list.slider .wrapper p-video-item {
      min-width: 43vw;
    }
  }

  @container (max-width: 450px) {
    .video-list.slider .wrapper p-video-item {
      min-width: 75vw;
    }
  }
`;
